/**
 * Per-session runtime — owns the entire lifecycle of one agent
 * conversation: WebSocket transport, reducer state, history paging,
 * and reconnect bookkeeping. Framework-agnostic by design; React
 * binds via :class:`AgentPanelProvider` and ``useSyncExternalStore``.
 *
 * One runtime exists per active ``(cwd, sessionId)``. The provider
 * holds at most one runtime at a time — when the user switches
 * sessions, the prior runtime is disposed and a fresh one is
 * created. Background sessions stay alive on the *server* (the
 * workspace server's :class:`LiveSession` keeps the SDK subprocess
 * running across WebSocket drops); the browser is a thin viewer
 * that reattaches via ``?since_seq=`` replay.
 *
 * The runtime emits a single coarse "snapshot changed" signal to
 * its subscribers; consumers re-read :meth:`getSnapshot` to pick
 * up the new state. This matches React's
 * :func:`useSyncExternalStore` contract precisely so the foreground
 * runtime can drive the chat surface with no per-frame reducer
 * coupling in React-land.
 */

import * as Sentry from '@sentry/react';
import { type AgentSessionEntry, fetchSessionInfo, fetchSessionMessages, renameSession } from './api';
import type { TurnDisplay } from './reducer';
import {
  type AgentPanelState,
  applyInterrupt,
  applyOptimisticEffort,
  applyOptimisticInteractionResponse,
  applyOptimisticPermissionMode,
  applyOptimisticSpeed,
  applyOptimisticUserSend,
  applySessionStateSnapshot,
  applyTaskStopped,
  type ConnectionStatus,
  clearErrors as clearErrorsReducer,
  initialState,
  isTerminalTaskStatus,
  latestContextTokens,
  prependHistory,
  reduce,
  resetAttached,
  resetTurns,
} from './reducer';
import { AgentTransport } from './transport';
import type { AgentSpeed, AttachmentRole, InboundFrame, PermissionMode, SessionAttachedFrame } from './types';

// =============================================================================
// Tunables
// =============================================================================

/**
 * Tail page size for the initial chat history hydrate. Picked to land
 * several screens of recent context up front so the user can scroll
 * back without immediately tripping a fetch; older history is paginated
 * in :data:`HISTORY_PAGE_SIZE` chunks from there.
 */
const HISTORY_TAIL_SIZE = 50;
const HISTORY_PAGE_SIZE = 50;

// Upper bound on the recently-completed-task-id set (FIFO). Large enough to
// cover any plausible in-flight-history window, small enough to stay cheap.
const MAX_RECENTLY_COMPLETED_TASKS = 256;
// Reconcile period for the active-task chip sweeper. Gated on
// ``hasActiveTasks`` + tab visibility so it only runs while chips are
// actually on screen. It reconciles on every tick regardless of WS frame
// activity: a leaked chip is a *missed* terminal ``task_notification``,
// which an unrelated recent frame cannot certify away (see
// ``_syncActiveTaskRefreshTimer``).
const ACTIVE_TASK_STATE_REFRESH_MS = 3_000;

/**
 * Reconnect backoff parameters.
 *
 * A WS that hammers a hard-down server in a tight loop is the
 * pathology this defends against. Bounded attempts + capped
 * exponential backoff + symmetric jitter + an "open grace" window
 * (so a flap-loop doesn't reset the attempt counter on every
 * accepted-then-dropped handshake) keep the worst case at ~6 attempts
 * spread over ~2 minutes.
 */
const MAX_RECONNECT_ATTEMPTS = 6;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_OPEN_GRACE_MS = 5_000;
const RECONNECT_JITTER = 0.25;

// =============================================================================
// Types
// =============================================================================

export interface SendInputAccepted {
  accepted: true;
  clientMessageId: string | null;
  acknowledged: boolean;
}

export type SendInputResult = false | SendInputAccepted;

export interface SessionRuntimeOptions {
  /**
   * Workspace the runtime is bound to, or ``null`` for local targets.
   * Agent sessions are workspace-server-resident, so this is part of
   * the runtime's identity: the provider uses it to refuse persisting
   * a snapshot from one workspace under another workspace's keys
   * during the one-render window of a direct workspace switch.
   */
  workspaceId?: string | null;
  /** Absolute project cwd. Bound for the lifetime of the runtime. */
  cwd: string;
  /**
   * Real session id to resume, or ``null`` for a brand-new
   * conversation. The runtime captures the server-assigned id from
   * the first ``session_attached`` frame either way.
   */
  resumeSessionId: string | null;
  /**
   * Reasoning effort override ("low" | "medium" | "high" | "xhigh"
   * | "max"). Sent as ``?effort=`` on connect; ``null`` lets the
   * server fall back to ``settings.json``.
   */
  effort: string | null;
  /**
   * Claude Code speed mode. ``fast`` enables Claude Code fast mode
   * for the spawned session; ``normal`` keeps the default path.
   * Changes require a reconnect because the option is passed through
   * the runtime settings JSON on startup.
   */
  speed?: AgentSpeed | null;
  /**
   * Claude Code permission mode. ``plan`` starts native plan mode;
   * ``bypassPermissions`` is the normal execute path.
   */
  permissionMode?: PermissionMode | null;
  /**
   * Whether the parent panel is visible. Drives whether the
   * runtime is attached to the workspace WebSocket. Hidden runtimes
   * keep their reducer snapshot but detach from the stream.
   */
  visible: boolean;
  /**
   * HTTPS origin of the connected workspace's Envoy
   * (``https://ws-XXXX.<env>.antioch.com``). Required in deployed
   * mode — the platform origin doesn't proxy ``/workspace/*`` to the
   * pod. ``null`` (the default) falls back to
   * ``window.location.origin`` for local dev where the Vite proxy
   * forwards to ``localhost:8899``.
   */
  baseUrl?: string | null;
  /**
   * Workspace JWT for Envoy auth, attached as a query-param to the
   * WS handshake. ``null`` (the default) skips the gate
   * (local dev / tests).
   */
  workspaceToken?: string | null;
}

/**
 * Public snapshot the runtime exposes to subscribers. Mirrors the
 * fields a React provider needs to render the chat surface for the
 * active session.
 */
export interface SessionRuntimeSnapshot {
  cwd: string;
  /**
   * Real session id once the server assigns one (drawn from
   * ``state.sessionId``); ``null`` for brand-new sessions that
   * haven't attached yet.
   */
  sessionId: string | null;
  state: AgentPanelState;
  connectionStatus: ConnectionStatus;
  effort: string | null;
  speed: AgentSpeed;
  permissionMode: PermissionMode;
  historyHasMore: boolean;
  historyLoading: boolean;
  /**
   * Cached metadata for the bound session (name, created_at,
   * last_modified). Refreshes after every ``session_attached`` or
   * rename.
   */
  currentSession: AgentSessionEntry | null;
  /**
   * One-shot "first paint ready" latch — the runtime has at least
   * once seen the WS attached, the initial history page resolved
   * (when resuming), and the session metadata fetched (when there
   * is a session id). After this flips it stays true even if the
   * WS reconnects.
   */
  sessionReady: boolean;
  /**
   * Role this attachment holds on the server-resident session.
   * ``driver`` may type / interrupt / stop tasks; ``viewer`` is
   * read-only (another client is currently driving).
   * ``null`` until the first ``session_attached`` envelope lands.
   */
  attachmentRole: AttachmentRole | null;
  /**
   * Number of clients (this one included) attached to the same
   * server-resident session. ``1`` for the common case; ``2+`` for
   * multi-tab / multi-device viewing. ``null`` before the first
   * ``session_attached`` envelope lands.
   */
  attachmentCount: number | null;
  pendingUserInputIds: string[];
  lastAcceptedClientMessageId: string | null;
}

/**
 * Internal mutable subset of the snapshot. Kept separate so we can
 * construct fresh frozen snapshots by spreading.
 */
interface MutableRuntime {
  state: AgentPanelState;
  connectionStatus: ConnectionStatus;
  effort: string | null;
  speed: AgentSpeed;
  permissionMode: PermissionMode;
  resumeSessionId: string | null;
  historyHasMore: boolean;
  historyLoading: boolean;
  currentSession: AgentSessionEntry | null;
  sessionReady: boolean;
  initialHistoryLoaded: boolean;
  initialMetadataLoaded: boolean;
  attachmentRole: AttachmentRole | null;
  attachmentCount: number | null;
  /**
   * Highest server-stamped seq we've observed on an outbound
   * frame. Sent as ``?since_seq=`` on reconnect so the server
   * can replay only the gap. Reset to 0 when the runtime starts
   * fresh (no resume id) or when the session id changes.
   */
  lastSeenSeq: number;
}

// =============================================================================
// SessionRuntime
// =============================================================================

/**
 * One live agent conversation.
 *
 * Owns transport, reducer state, history pagination, and reconnect
 * timers. No React inside. React subscribes via
 * :meth:`subscribe` + :meth:`getSnapshot`; both are stable across
 * the runtime's lifetime so ``useSyncExternalStore`` works without
 * extra wrapping.
 */
export class SessionRuntime {
  readonly workspaceId: string | null;
  readonly cwd: string;

  private _mut: MutableRuntime;
  private _snapshot: SessionRuntimeSnapshot;
  private _listeners: Set<() => void> = new Set();

  private _transport: AgentTransport | null = null;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempts = 0;
  private _openGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private _activeTaskRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private _historyOldestOffset: number | null = null;
  private _historyLoadedKey: string | null = null;
  // Task ids seen to terminate via a live ``task_notification``. A stale
  // in-flight history snapshot (server state captured before the completion
  // landed) must not resurrect them. Bounded FIFO so a long session can't grow
  // it without limit; aged-out ids are harmless — a snapshot old enough to
  // still list them would be rejected for other reasons.
  private readonly _recentlyCompletedTaskIds = new Set<string>();
  private _historyLoadingFlight = false;
  private _sessionStateRefreshFlight = false;
  private _resetTurnsOnNextHistoryApply = false;
  // ``client_message_id`` values for prompts the webapp has shipped
  // via ``user_input`` but the server hasn't acknowledged with
  // ``user_input_accepted`` yet. The compose surface reads the live
  // snapshot to gate its "clear the input field" effect on the ack.
  private _pendingUserInputs = new Set<string>();
  private _lastAcceptedClientMessageId: string | null = null;

  private _visible: boolean;
  private _disposed = false;
  private _metadataFetchToken = 0;
  /**
   * Connection context (workspace Envoy origin + JWT) is mutable so a
   * long-running runtime survives the hourly workspace_token rotation
   * from ``ServiceTokensProvider``: when the parent calls
   * :meth:`setConnection`, the next ``_openSocket`` reads the latest
   * values, so a reconnect that lands after a token rotation uses the
   * fresh token instead of the one captured at construction time.
   * Updating these fields does NOT close the active WebSocket — the
   * current connection's URL is already in flight; we only care about
   * what the *next* reconnect uses.
   */
  private _baseUrl: string | null;
  private _workspaceToken: string | null;
  constructor(opts: SessionRuntimeOptions) {
    this.workspaceId = opts.workspaceId ?? null;
    this.cwd = opts.cwd;
    this._visible = opts.visible;
    this._baseUrl = opts.baseUrl ?? null;
    this._workspaceToken = opts.workspaceToken ?? null;
    const permissionMode = opts.permissionMode ?? 'bypassPermissions';
    this._mut = {
      state: applyOptimisticPermissionMode(initialState(), permissionMode),
      connectionStatus: 'idle',
      effort: opts.effort,
      speed: opts.speed ?? 'normal',
      permissionMode,
      resumeSessionId: opts.resumeSessionId,
      historyHasMore: false,
      historyLoading: false,
      currentSession: null,
      sessionReady: false,
      initialHistoryLoaded: false,
      initialMetadataLoaded: false,
      attachmentRole: null,
      attachmentCount: null,
      lastSeenSeq: 0,
    };
    this._snapshot = this._buildSnapshot();
  }

  // ===========================================================================
  // Subscribe / snapshot
  // ===========================================================================

  /** React-style subscribe. Returns an unsubscribe callback. */
  subscribe = (listener: () => void): (() => void) => {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  };

  /** Immutable, structurally-stable snapshot of the runtime's state. */
  getSnapshot = (): SessionRuntimeSnapshot => this._snapshot;

  // ===========================================================================
  // Identity
  // ===========================================================================

  /**
   * The user-intended resume id (what the user clicked) — falls
   * back to the server-assigned id once attached. Drives the
   * sidebar's row highlight optimistically.
   */
  get resumeSessionId(): string | null {
    return this._mut.resumeSessionId;
  }

  /** Real session id from the server, once attached. */
  get sessionId(): string | null {
    return this._mut.state.sessionId;
  }

  /** Server-attached session id, ``null`` while the WS is dialing. */
  get attached(): boolean {
    return this._mut.state.attached;
  }

  // ===========================================================================
  // Connection lifecycle
  // ===========================================================================

  /**
   * Bring the socket up. Idempotent — reuses the existing transport
   * when one is already open or connecting.
   */
  connect(): void {
    if (this._disposed) return;
    if (this._mut.connectionStatus === 'open' || this._mut.connectionStatus === 'connecting') return;
    this._openSocket();
  }

  /**
   * Tear the live socket down without disposing the runtime.
   *
   * By default this clears attachment-derived affordances such as
   * active task chips and the completed-turn footer. Visibility
   * detaches pass preservation flags so returning to the tab stays
   * visually continuous until the next history snapshot reconciles
   * server truth. Effort changes only preserve completed-turn footer
   * state because they intentionally reconnect under a new run config.
   */
  disconnect(options: { preserveCompletedTurnWork?: boolean; preserveActiveTasks?: boolean } = {}): void {
    this._clearReconnectTimer();
    this._clearOpenGraceTimer();
    this._transport?.close();
    this._transport = null;
    // User-initiated disconnects (effort change, dispose) shouldn't
    // carry the prior session's failure budget into the next
    // reconnect attempt — otherwise after a few flaky cycles the
    // runtime can't reconnect at all without manual intervention.
    this._reconnectAttempts = 0;
    this._patch({ connectionStatus: 'closed' }, () =>
      resetAttached(this._mut.state, {
        preserveCompletedTurnWork: options.preserveCompletedTurnWork,
        preserveActiveTasks: options.preserveActiveTasks,
      })
    );
  }

  /**
   * Force a fresh dial — clears sticky errors and resets the
   * attempt counter so the user's manual "Retry" gesture from the
   * crash screen always tries at least once more.
   */
  retry(): void {
    if (this._disposed) return;
    this.disconnect();
    this._reconnectAttempts = 0;
    this._patch({}, () => clearErrorsReducer(this._mut.state));
    this._openSocket();
  }

  /**
   * Update the panel's visibility flag. Background runtimes still
   * keep their reducer state, but they detach from the workspace
   * WebSocket so hidden tabs do not keep ingesting an unbounded
   * live transcript. The workspace server's LiveSession stays alive
   * and replay/history rehydrate fills the gap when the user returns.
   */
  setVisibility(visible: boolean): void {
    if (this._visible === visible) return;
    this._visible = visible;
    if (!visible) {
      this._clearActiveTaskRefreshTimer();
      this.disconnect({ preserveCompletedTurnWork: true, preserveActiveTasks: true });
      return;
    }
    this._syncActiveTaskRefreshTimer();
    if (
      visible &&
      this._mut.connectionStatus !== 'open' &&
      this._mut.connectionStatus !== 'connecting' &&
      this._reconnectTimer == null
    ) {
      this._openSocket();
    }
  }

  /**
   * Update the workspace routing + auth context this runtime uses for
   * the next reconnect. Does NOT close the active socket — the
   * in-flight WS already authenticated against whatever was current
   * when its handshake fired, and an established WS doesn't re-auth.
   * The point is that *if* the WS drops (network blip, server
   * restart, kubelet eviction) after the workspace_token rotates on
   * the hourly ``ServiceTokensProvider`` cadence, the auto-reconnect
   * picks up the fresh token instead of replaying the stale one
   * Envoy would now reject. Idempotent on identical inputs.
   */
  setConnection(opts: { baseUrl: string | null; workspaceToken: string | null }): void {
    this._baseUrl = opts.baseUrl;
    this._workspaceToken = opts.workspaceToken;
  }

  /**
   * Dispose the runtime. Closes the socket, cancels reconnect
   * timers, and clears subscribers. After this the runtime is
   * inert — the pool drops its reference.
   */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._clearReconnectTimer();
    this._clearOpenGraceTimer();
    this._clearActiveTaskRefreshTimer();
    this._transport?.close();
    this._transport = null;
    this._listeners.clear();
  }

  // ===========================================================================
  // Public commands
  // ===========================================================================

  /**
   * Ship a user prompt and wait for the workspace server to ack it.
   *
   * The visible user bubble is inserted only after the server accepts
   * the frame and successfully submits it to the SDK. That keeps
   * browser-side sends from disappearing across refreshes when a
   * WebSocket drops immediately after ``send`` returns.
   *
   * No-op when this attachment is a viewer: another client is
   * currently driving this server-resident session, so our typing
   * would be rejected with a ``server_error`` anyway. Returning
   * early avoids a flash of optimistic UI for a frame that's
   * about to be denied.
   */
  sendInput(text: string, display?: TurnDisplay): SendInputResult {
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (this._isViewer()) return false;
    if (!this._isTransportOpen()) return false;
    const clientMessageId = this._newClientMessageId();
    const previousState = this._mut.state;
    this._patch({}, () => applyOptimisticUserSend(this._mut.state, trimmed, display, clientMessageId));
    if (!this._send({ type: 'user_input', text: trimmed, client_message_id: clientMessageId })) {
      this._patch({}, () => previousState);
      return false;
    }
    this._pendingUserInputs.add(clientMessageId);
    this._snapshot = this._buildSnapshot();
    for (const listener of this._listeners) listener();
    return { accepted: true, clientMessageId, acknowledged: false };
  }

  respondToInteraction(
    requestId: string,
    decision: 'allow' | 'deny',
    updatedInput?: Record<string, unknown>,
    message?: string
  ): void {
    if (!requestId) return;
    if (this._isViewer()) return;
    const request = this._mut.state.pendingInteractions.find((item) => item.request_id === requestId);
    const shouldSwitchToExecute = decision === 'allow' && request?.kind === 'exit_plan_mode';
    const previousState = this._mut.state;
    this._patch({}, () => applyOptimisticInteractionResponse(this._mut.state, requestId));
    if (
      !this._send({
        type: 'interaction_response',
        request_id: requestId,
        decision,
        updated_input: updatedInput,
        message,
      })
    ) {
      this._patch({}, () => previousState);
      return;
    }
    if (shouldSwitchToExecute) {
      this.setPermissionMode('bypassPermissions');
    }
  }

  /** Send an interrupt frame and tag the trailing assistant turn. */
  interrupt(): void {
    if (this._isViewer()) return;
    if (!this._isTransportOpen()) return;
    this._patch({}, () => applyInterrupt(this._mut.state));
    this._send({ type: 'interrupt' });
  }

  /**
   * :return: ``true`` when this attachment is a viewer rather
   *     than the driver. Mutating commands no-op in this state.
   */
  private _isViewer(): boolean {
    return this._mut.attachmentRole === 'viewer';
  }

  /**
   * Switch the SDK's reasoning effort. The spawned CLI bakes
   * ``--effort`` in at startup, so a real change requires a full
   * reconnect; we drop the live socket and immediately redial with
   * the new ``?effort=`` query param. The optimistic effort flip
   * keeps the pill snappy; the server's ``session_attached`` echo
   * is the source of truth.
   */
  setEffort(effort: string): void {
    if (!effort) return;
    if (this._isViewer()) return;
    if (this._mut.effort === effort) return;
    this._mut.effort = effort;
    this._patch({ effort }, () => applyOptimisticEffort(this._mut.state, effort));
    this._reconnectForStartupConfigChange();
    if (this._visible) this._openSocket({ preserveCompletedTurnWork: true, preserveActiveTasks: false });
  }

  /**
   * Toggle Claude Code fast mode for this session. The current Agent
   * SDK exposes this through the settings JSON, so a real change
   * requires the same startup-config reconnect as effort/context.
   */
  setSpeed(speed: AgentSpeed): void {
    if (!isAgentSpeed(speed)) return;
    if (this._isViewer()) return;
    if (this._mut.speed === speed) return;
    this._mut.speed = speed;
    this._patch({ speed }, () => applyOptimisticSpeed(this._mut.state, speed));
    this._reconnectForStartupConfigChange();
    if (this._visible) this._openSocket({ preserveCompletedTurnWork: true, preserveActiveTasks: false });
  }

  setPermissionMode(mode: PermissionMode): void {
    if (!isPermissionMode(mode)) return;
    if (this._isViewer()) return;
    if (this._mut.permissionMode === mode) return;
    const previousMode = this._mut.permissionMode;
    const previousState = this._mut.state;
    this._mut.permissionMode = mode;
    this._patch({ permissionMode: mode }, () => applyOptimisticPermissionMode(this._mut.state, mode));
    if (this._isTransportOpen()) {
      if (!this._send({ type: 'set_permission_mode', mode })) {
        this._mut.permissionMode = previousMode;
        this._patch({ permissionMode: previousMode }, () => previousState);
      }
    }
  }

  private _reconnectForStartupConfigChange(): void {
    this._clearReconnectTimer();
    this._clearOpenGraceTimer();
    const transport = this._transport;
    this._transport = null;
    transport?.close();
    this._reconnectAttempts = 0;
    this._patch({ connectionStatus: 'closed' }, () =>
      resetAttached(this._mut.state, { preserveCompletedTurnWork: true })
    );
  }

  stopTask(taskId: string): void {
    if (!taskId) return;
    if (this._isViewer()) return;
    // Optimistic-first, mirroring ``interrupt``: drop the chip from
    // the activity panel immediately so the click feels instant, then
    // ship the WS frame. If the transport is closed (rare, only
    // during reconnect windows) the chip stays optimistically gone --
    // the next ``session_state`` refresh reconciles either way, and
    // a missed kill is less jarring than a frozen "Kill" button.
    this._patch({}, () => applyTaskStopped(this._mut.state, taskId));
    this._send({ type: 'stop_task', task_id: taskId });
  }

  clearErrors(): void {
    this._patch({}, () => clearErrorsReducer(this._mut.state));
  }

  /**
   * Page in older history. No-op when already loading or when the
   * session has no more history on disk.
   */
  async loadOlderHistory(): Promise<void> {
    if (this._historyLoadingFlight) return;
    if (!this._mut.historyHasMore) return;
    const before = this._historyOldestOffset;
    if (before == null || before <= 0) return;
    const sessionId = this._mut.state.sessionId ?? this._mut.resumeSessionId;
    if (!sessionId) return;

    this._historyLoadingFlight = true;
    this._patch({ historyLoading: true });
    try {
      const page = await fetchSessionMessages(this.cwd, sessionId, {
        limit: HISTORY_PAGE_SIZE,
        before,
        includeState: false,
      });
      if (this._disposed) return;
      if (page.messages.length > 0) {
        this._patch({}, () => prependHistory(this._mut.state, page.messages));
      }
      this._historyOldestOffset = page.start_offset;
      this._patch({ historyHasMore: page.has_more });
    } catch (err) {
      console.warn('SessionRuntime: failed to load older history', err);
    } finally {
      this._historyLoadingFlight = false;
      if (!this._disposed) this._patch({ historyLoading: false });
    }
  }

  /**
   * Rename the session in-place. Refreshes ``currentSession`` so
   * the header label updates without a manual refetch.
   */
  async renameCurrentSession(title: string): Promise<void> {
    const trimmed = title.trim();
    const sessionId = this._mut.state.sessionId;
    if (!trimmed || !sessionId) return;
    await renameSession(this.cwd, sessionId, trimmed);
    await this._refreshSessionMetadata();
  }

  // ===========================================================================
  // Internals — transport
  // ===========================================================================

  private _openSocket(options: { preserveCompletedTurnWork?: boolean; preserveActiveTasks?: boolean } = {}): void {
    if (this._disposed) return;
    this._clearReconnectTimer();
    this._clearOpenGraceTimer();
    this._transport?.close();
    // Reset ``attached`` before the new socket comes up so the status
    // chip in the input footer doesn't briefly flash a stale "Ready"
    // from the previous session while we wait for ``session_attached``.
    this._patch({ connectionStatus: 'connecting' }, () =>
      resetAttached(this._mut.state, {
        preserveCompletedTurnWork: options.preserveCompletedTurnWork ?? true,
        preserveActiveTasks: options.preserveActiveTasks ?? true,
      })
    );

    const transport = new AgentTransport({
      cwd: this.cwd,
      resumeSessionId: this._mut.resumeSessionId ?? undefined,
      effort: this._mut.effort ?? undefined,
      speed: this._mut.speed,
      permissionMode: this._mut.permissionMode,
      sinceSeq: this._mut.lastSeenSeq,
      baseUrl: this._baseUrl ?? undefined,
      workspaceToken: this._workspaceToken,
      // Each handler guards on ``_transport === transport`` because a
      // previous transport's WebSocket can fire close / error after
      // we've already started a new one (e.g. ``setEffort`` reopened
      // mid-flight). Without the guard the stale callback stomps live
      // status and schedules a reconnect with stale parameters.
      onFrame: (frame) => {
        if (this._transport !== transport) return;
        // Track the highest seq we've seen so the next reconnect can
        // ask the server to replay only the gap via ``?since_seq=``.
        const seq = (frame as { seq?: number }).seq;
        if (typeof seq === 'number' && seq > this._mut.lastSeenSeq) {
          this._mut.lastSeenSeq = seq;
        }
        const runtimePatch: Partial<MutableRuntime> = {};
        if (frame.type === 'permission_mode_changed' && isPermissionMode(frame.permission_mode)) {
          runtimePatch.permissionMode = frame.permission_mode;
        }
        if (frame.type === 'session_attached' && isAgentSpeed(frame.speed)) {
          runtimePatch.speed = frame.speed;
        }
        if (frame.type === 'user_input_accepted') {
          this._handleUserInputAccepted(frame.client_message_id);
        }
        if (frame.type === 'task_started' && frame.task_id) {
          // A re-used task id is active again — drop it from the completed set
          // so a later snapshot is allowed to keep it.
          this._recentlyCompletedTaskIds.delete(frame.task_id);
        }
        if (frame.type === 'task_notification' && isTerminalTaskStatus(frame.status)) {
          this._noteCompletedTask(frame.task_id);
        }
        const highWaterBefore = this._mut.state.contextTokensHighWater;
        this._patch(runtimePatch, () => reduce(this._mut.state, frame));
        // Track every change to the context-tokens high-water mark.
        // The chip now shows ``latestContextTokens`` instead, but the
        // high water is still useful telemetry -- if a user reports
        // the chip looking stuck, the breadcrumb trail tells us
        // whether the per-iteration sum is genuinely flat or whether
        // we silently dropped a ``message_start.usage`` somewhere
        // along the way.
        const highWaterAfter = this._mut.state.contextTokensHighWater;
        if (highWaterBefore !== highWaterAfter) {
          Sentry.addBreadcrumb({
            category: 'agent_context',
            message: 'context_high_water',
            level: 'info',
            data: {
              frame: frame.type,
              before: highWaterBefore,
              after: highWaterAfter,
              latest: latestContextTokens(this._mut.state),
              compactEpoch: this._mut.state.compactEpoch,
            },
          });
        }
        if (frame.type === 'server_error') {
          this._maybeRetryPoisonedSession(frame.error);
        }
        if (
          (frame.type === 'result' || frame.type === 'system.compact_boundary') &&
          frame.last_completed_turn_duration_ms !== undefined
        ) {
          this._patchCurrentSession({ is_active: this._mut.state.awaitingAssistant });
        }
        if (frame.type === 'system.compact_boundary') {
          const data = (frame.data ?? {}) as Record<string, unknown>;
          const compactMetadata =
            data.compactMetadata && typeof data.compactMetadata === 'object'
              ? (data.compactMetadata as Record<string, unknown>)
              : null;
          const trigger =
            typeof data.trigger === 'string'
              ? data.trigger
              : typeof compactMetadata?.trigger === 'string'
                ? (compactMetadata.trigger as string)
                : 'unknown';
          const preTokens = typeof data.pre_tokens === 'number' ? data.pre_tokens : null;
          const postTokens = typeof data.post_tokens === 'number' ? data.post_tokens : null;
          Sentry.addBreadcrumb({
            category: 'agent_compact',
            message: 'compact_boundary',
            level: 'info',
            data: {
              trigger,
              preTokens,
              postTokens,
              compactEpoch: this._mut.state.compactEpoch,
              awaitingAssistant: this._mut.state.awaitingAssistant,
            },
          });
        }
        if (frame.type === 'session_attached') {
          this._handleAttachEnvelope(frame);
        } else if (frame.type === 'attachment_role_changed') {
          this._mut.attachmentRole = frame.role;
          this._snapshot = this._buildSnapshot();
          for (const listener of this._listeners) listener();
        }
        // ``session_attached`` carries the server-assigned session id
        // (in ``frame.resume`` for resumes, in ``state.sessionId`` for
        // fresh sessions once the SDK init lands). Trigger a metadata
        // refresh whenever it advances.
        if (frame.type === 'session_attached' || frame.type === 'system.init') {
          if (this._mut.state.sessionId && this._mut.state.sessionId !== this._mut.resumeSessionId) {
            this._mut.resumeSessionId = this._mut.state.sessionId;
            // Session id changed — our seq cursor is for the prior
            // session and meaningless for the new one. Reset.
            this._mut.lastSeenSeq = 0;
            this._snapshot = this._buildSnapshot();
          }
          const historyFetchStarted = this._maybeFetchHistory();
          if (!historyFetchStarted && frame.type === 'session_attached') {
            this._refreshSessionState();
          }
          this._refreshSessionMetadata();
        }
        this._maybeLatchSessionReady();
      },
      onClose: () => {
        if (this._transport !== transport) return;
        this._clearOpenGraceTimer();
        this._transport = null;
        this._pendingUserInputs.clear();
        // Detach state on socket close so the status pill collapses
        // to "Disconnected" instead of staying stuck at "Thinking" on
        // top of the half-rendered last turn.
        this._patch({ connectionStatus: 'closed' }, () =>
          resetAttached(this._mut.state, { preserveCompletedTurnWork: true, preserveActiveTasks: true })
        );
        this._maybeScheduleReconnect();
      },
      onError: () => {
        if (this._transport !== transport) return;
        this._patch({ connectionStatus: 'error' });
      },
    });
    this._transport = transport;

    transport
      .connect()
      .then(() => {
        if (this._transport !== transport) return;
        this._patch({ connectionStatus: 'open' });
        // Only reset the attempt counter after the WS has been
        // continuously open for ``RECONNECT_OPEN_GRACE_MS``. A server
        // that accepts the handshake then drops it would otherwise
        // reset attempts on every brief open and keep the loop running
        // forever at the base delay — that is the spam mode this whole
        // block is defending against.
        this._clearOpenGraceTimer();
        this._openGraceTimer = setTimeout(() => {
          this._openGraceTimer = null;
          if (this._transport !== transport) return;
          this._reconnectAttempts = 0;
        }, RECONNECT_OPEN_GRACE_MS);
        this._maybeFetchHistory();
      })
      .catch((err) => {
        if (this._transport !== transport) return;
        this._patch({ connectionStatus: 'error' });
        // ``onClose`` typically fires next on most browsers — that
        // handler owns the backoff scheduling. We don't schedule a
        // retry here to avoid two timers stacking.
        console.error('SessionRuntime: connect failed', err);
      });
  }

  private _maybeScheduleReconnect(): void {
    if (this._disposed) return;
    if (!this._visible) return;
    const attempts = this._reconnectAttempts;
    if (attempts >= MAX_RECONNECT_ATTEMPTS) {
      this._patch({ connectionStatus: 'error' });
      return;
    }
    const delay = this._computeReconnectDelay(attempts);
    this._reconnectAttempts = attempts + 1;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._openSocket();
    }, delay);
  }

  private _computeReconnectDelay(attempts: number): number {
    const exponential = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempts);
    const jitter = 1 + (Math.random() * 2 - 1) * RECONNECT_JITTER;
    return Math.round(exponential * jitter);
  }

  private _clearReconnectTimer(): void {
    if (this._reconnectTimer != null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  private _clearOpenGraceTimer(): void {
    if (this._openGraceTimer != null) {
      clearTimeout(this._openGraceTimer);
      this._openGraceTimer = null;
    }
  }

  private _clearActiveTaskRefreshTimer(): void {
    if (this._activeTaskRefreshTimer != null) {
      clearTimeout(this._activeTaskRefreshTimer);
      this._activeTaskRefreshTimer = null;
    }
  }

  /**
   * Reconcile live background-task chips against the persisted
   * session-state snapshot while the session is visible. The WebSocket
   * should deliver terminal ``task_notification`` frames, but if the
   * browser misses one during a reconnect or SDK hiccup, the full
   * transcript scan retires the stale shell chip without requiring a
   * manual session switch.
   */
  private _syncActiveTaskRefreshTimer(): void {
    const hasActiveTasks = Object.keys(this._mut.state.activity.activeTasks).length > 0;
    if (this._disposed || !this._visible || !hasActiveTasks) {
      this._clearActiveTaskRefreshTimer();
      return;
    }
    if (this._activeTaskRefreshTimer != null) return;
    this._activeTaskRefreshTimer = setTimeout(() => {
      this._activeTaskRefreshTimer = null;
      if (this._disposed || !this._visible) return;
      if (Object.keys(this._mut.state.activity.activeTasks).length === 0) return;
      // Reconcile unconditionally while chips are shown. We deliberately do
      // NOT skip when a WS frame arrived recently: a leaked chip is a
      // *missed* terminal ``task_notification``, and a recent unrelated
      // frame (``stream_event``, another task's ``task_progress``, assistant
      // text) says nothing about the leaked task. The old frame-recency gate
      // meant the sweep never ran during active work — exactly when
      // background shells start/stop and a terminal frame is most likely
      // dropped — so dead shell/monitor chips piled up until the agent went
      // quiet for >3s. The fetch is cheap (``limit:1`` + session_state),
      // single-flighted via ``_sessionStateRefreshFlight``, and only armed
      // while tasks exist and the tab is visible, so a 3s cadence is bounded.
      this._refreshSessionState();
      this._syncActiveTaskRefreshTimer();
    }, ACTIVE_TASK_STATE_REFRESH_MS);
  }

  private _send(frame: InboundFrame): boolean {
    return this._transport?.send(frame) ?? false;
  }

  private _handleUserInputAccepted(clientMessageId: string): void {
    if (!this._pendingUserInputs.delete(clientMessageId)) return;
    this._lastAcceptedClientMessageId = clientMessageId;
    this._snapshot = this._buildSnapshot();
    for (const listener of this._listeners) listener();
  }

  private _newClientMessageId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `input-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private _maybeRetryPoisonedSession(error: string): void {
    if (!error.includes('session crashed') || !error.includes('reconnect to resume')) return;
    queueMicrotask(() => {
      if (!this._disposed) this.retry();
    });
  }

  private _isTransportOpen(): boolean {
    return this._transport?.isOpen === true;
  }

  // ===========================================================================
  // Internals — async data
  // ===========================================================================

  /**
   * Consume the new server-resident metadata on a
   * ``session_attached`` envelope.
   *
   * The server reports:
   *   - ``attachment_role``: driver vs viewer (multi-tab UX).
   *   - ``attachment_count``: how many clients see this session.
   *   - ``last_seq``: ring head at attach time. When replay succeeds,
   *     individual replayed frames advance our cursor as they arrive.
   *     When replay fails, this becomes the new floor after we force a
   *     history rehydrate.
   *   - ``replayed``: whether the server filled the gap from our
   *     ``since_seq`` to ``last_seq`` from the ring. When
   *     ``false`` on a reconnect, the gap was too large; the
   *     existing ``_maybeFetchHistory`` path re-hydrates from
   *     ``/project/sessions/messages``.
   */
  private _handleAttachEnvelope(frame: SessionAttachedFrame): void {
    const role = frame.attachment_role ?? 'driver';
    const count = frame.attachment_count ?? null;
    let changed = false;
    if (this._mut.attachmentRole !== role) {
      this._mut.attachmentRole = role;
      changed = true;
    }
    if (this._mut.attachmentCount !== count) {
      this._mut.attachmentCount = count;
      changed = true;
    }
    // ``replayed=false`` after we have already observed live frames
    // means the server's ring window dropped our gap — frames we
    // missed during the disconnect are no longer recoverable from
    // the ring. Force a fresh REST hydrate and defer the transcript
    // reset until that fetch succeeds; a transient REST error should
    // not blank the panel while the old visible context is still the
    // best thing we have.
    if (frame.replayed === false && this._mut.lastSeenSeq > 0) {
      this._historyLoadedKey = null;
      this._historyOldestOffset = null;
      this._mut.initialHistoryLoaded = false;
      this._resetTurnsOnNextHistoryApply = true;
    }
    // Only advance to the attach head when replay failed. On a
    // successful replay, the replay frames themselves advance the
    // cursor as they are delivered. Advancing here would mean a
    // disconnect between the envelope and the replay body permanently
    // skips those frames on the next reconnect.
    if (typeof frame.last_seq === 'number' && (frame.replayed === false || frame.last_seq < this._mut.lastSeenSeq)) {
      this._mut.lastSeenSeq = frame.last_seq;
    }
    if (isPermissionMode(frame.permission_mode)) {
      const serverMode = frame.permission_mode;
      const desiredMode = this._mut.permissionMode;
      if (serverMode === desiredMode || role === 'viewer') {
        if (this._mut.permissionMode !== serverMode) {
          this._mut.permissionMode = serverMode;
          changed = true;
        }
      } else if (this._isTransportOpen()) {
        this._send({ type: 'set_permission_mode', mode: desiredMode });
        this._patch({}, () => applyOptimisticPermissionMode(this._mut.state, desiredMode));
      }
    }
    if (changed) {
      this._snapshot = this._buildSnapshot();
      for (const listener of this._listeners) listener();
    }
  }

  private _patchCurrentSession(patch: Partial<AgentSessionEntry>): void {
    const currentSession = this._mut.currentSession;
    if (currentSession === null) return;
    this._patch({ currentSession: { ...currentSession, ...patch } });
  }

  /**
   * Record a task id that terminated via a live frame so a later stale
   * history snapshot can't resurrect it. Bounded FIFO (oldest evicted).
   */
  private _noteCompletedTask(taskId: string | undefined): void {
    if (!taskId) return;
    // Delete-then-add refreshes recency so a re-completed id stays in window.
    this._recentlyCompletedTaskIds.delete(taskId);
    this._recentlyCompletedTaskIds.add(taskId);
    if (this._recentlyCompletedTaskIds.size > MAX_RECENTLY_COMPLETED_TASKS) {
      const oldest = this._recentlyCompletedTaskIds.values().next().value;
      if (oldest !== undefined) this._recentlyCompletedTaskIds.delete(oldest);
    }
  }

  /**
   * Hydrate the chat history tail the first time we know a real
   * session id for this runtime. Idempotent across re-attaches —
   * the loaded key is kept across reconnects, but the runtime
   * itself is per-session so the key is implicit.
   */
  private _maybeFetchHistory(): boolean {
    if (this._disposed) return false;
    const sessionId = this._mut.resumeSessionId;
    if (!sessionId) return false;
    const key = `${this.cwd}|${sessionId}`;
    if (this._historyLoadedKey === key) return false;
    // Claim the key synchronously, before the await below, so the two legitimate
    // triggers for the initial hydrate -- the socket-open handler and the
    // ``session_attached`` frame -- don't each launch an in-flight fetch. A
    // duplicate in-flight fetch races: its stale ``session_state`` can resurrect
    // tasks that completed between the two requests, and its older ``start_offset``
    // clobbers the pagination cursor. Reset to ``null`` on error so a failed
    // hydrate still retries on the next attach.
    this._historyLoadedKey = key;

    void (async () => {
      try {
        const requestedAt = Date.now();
        const activeTaskIdsAtRequest = new Set(Object.keys(this._mut.state.activity.activeTasks));
        const contextTokensHighWaterAtRequest = this._mut.state.contextTokensHighWater;
        const page = await fetchSessionMessages(this.cwd, sessionId, { limit: HISTORY_TAIL_SIZE });
        if (this._disposed) return;
        if (this._resetTurnsOnNextHistoryApply) {
          this._patch({}, () => resetTurns(this._mut.state));
          this._resetTurnsOnNextHistoryApply = false;
        }
        if (page.messages.length > 0) {
          this._patch({}, () => prependHistory(this._mut.state, page.messages));
        }
        if (page.session_state) {
          this._patch({}, () =>
            applySessionStateSnapshot(this._mut.state, page.session_state!, {
              preserveActiveTasksStartedAfter: requestedAt,
              activeTaskIdsAtRequest,
              contextTokensHighWaterAtRequest,
              completedTaskIds: this._recentlyCompletedTaskIds,
            })
          );
        }
        this._historyOldestOffset = page.start_offset;
        this._patch({ historyHasMore: page.has_more, initialHistoryLoaded: true });
        this._maybeLatchSessionReady();
      } catch (err) {
        console.warn('SessionRuntime: failed to load session history', err);
        if (!this._disposed) {
          this._historyLoadedKey = null;
          this._patch({ initialHistoryLoaded: true });
          this._maybeLatchSessionReady();
        }
      }
    })();
    return true;
  }

  /**
   * Refresh only the server-derived session state after reattaching to
   * a cached transcript. The message window may already be hydrated,
   * but ``is_active`` / active turn timing / active tasks can change
   * while this browser runtime is hidden behind another selected
   * session.
   */
  private _refreshSessionState(): void {
    if (this._disposed || this._sessionStateRefreshFlight) return;
    const sessionId = this._mut.resumeSessionId;
    if (!sessionId) return;

    this._sessionStateRefreshFlight = true;
    void (async () => {
      try {
        const requestedAt = Date.now();
        const activeTaskIdsAtRequest = new Set(Object.keys(this._mut.state.activity.activeTasks));
        const contextTokensHighWaterAtRequest = this._mut.state.contextTokensHighWater;
        const page = await fetchSessionMessages(this.cwd, sessionId, { limit: 1, includeState: true });
        if (this._disposed) return;
        if (page.session_state) {
          this._patch({}, () =>
            applySessionStateSnapshot(this._mut.state, page.session_state!, {
              preserveActiveTasksStartedAfter: requestedAt,
              activeTaskIdsAtRequest,
              contextTokensHighWaterAtRequest,
            })
          );
          // Reconciliation retired any task IDs that were active at
          // request time but no longer present in the snapshot. Those
          // represent WS terminal frames we missed (server crash mid-
          // flight, browser tab backgrounded across a disconnect, etc.).
          // Emit a warning breadcrumb so Sentry events carry the signal;
          // it should be rare and worth flagging when it isn't.
          const activeTaskIdsAfter = new Set(Object.keys(this._mut.state.activity.activeTasks));
          const retiredIds = [...activeTaskIdsAtRequest].filter((id) => !activeTaskIdsAfter.has(id));
          if (retiredIds.length > 0) {
            Sentry.addBreadcrumb({
              category: 'agent_reconcile',
              message: 'stale_task_retired',
              level: 'warning',
              data: {
                retiredCount: retiredIds.length,
                retiredIds: retiredIds.slice(0, 8),
                sessionId,
                requestedAt,
              },
            });
          }
        }
      } catch (err) {
        console.warn('SessionRuntime: failed to refresh session state', err);
      } finally {
        this._sessionStateRefreshFlight = false;
      }
    })();
  }

  /**
   * Refresh the cached session metadata from the workspace server's
   * directory listing. Token-based so a stale fetch from the
   * previous session id can't overwrite a fresh one.
   */
  private _refreshSessionMetadata(): void {
    const sessionId = this._mut.state.sessionId;
    if (!sessionId) return;
    const token = ++this._metadataFetchToken;
    void (async () => {
      try {
        const found = await fetchSessionInfo(this.cwd, sessionId);
        if (this._disposed || token !== this._metadataFetchToken) return;
        this._patch({ currentSession: found, initialMetadataLoaded: true });
        this._maybeLatchSessionReady();
      } catch {
        if (!this._disposed && token === this._metadataFetchToken) {
          this._patch({ currentSession: null, initialMetadataLoaded: true });
          this._maybeLatchSessionReady();
        }
      }
    })();
  }

  /**
   * Latch :attr:`sessionReady` once every initial fetch + the WS
   * attach has resolved. Stays true after that — a mid-chat
   * reconnect doesn't re-show the loader.
   */
  private _maybeLatchSessionReady(): void {
    if (this._mut.sessionReady) return;
    if (!this._mut.state.attached) return;
    if (this._mut.resumeSessionId !== null && !this._mut.initialHistoryLoaded) return;
    if (this._mut.state.sessionId !== null && !this._mut.initialMetadataLoaded) return;
    this._patch({ sessionReady: true });
  }

  // ===========================================================================
  // Internals — state mutation
  // ===========================================================================

  /**
   * Single chokepoint for state mutation. Applies the partial
   * mutable patch, optionally derives a fresh reducer state via
   * ``reducerUpdate``, builds a fresh snapshot, and notifies
   * subscribers — but only when something actually changed.
   */
  private _patch(patch: Partial<MutableRuntime>, reducerUpdate?: () => AgentPanelState): void {
    let changed = false;
    if (reducerUpdate) {
      const next = reducerUpdate();
      if (next !== this._mut.state) {
        this._mut.state = next;
        changed = true;
      }
    }
    const mutRecord = this._mut as unknown as Record<string, unknown>;
    for (const k of Object.keys(patch) as (keyof MutableRuntime)[]) {
      const v = patch[k];
      if (v === undefined) continue;
      if (mutRecord[k as string] !== v) {
        mutRecord[k as string] = v;
        changed = true;
      }
    }
    if (!changed) return;
    this._syncActiveTaskRefreshTimer();
    this._snapshot = this._buildSnapshot();
    for (const listener of this._listeners) listener();
  }

  private _buildSnapshot(): SessionRuntimeSnapshot {
    return {
      cwd: this.cwd,
      sessionId: this._mut.state.sessionId,
      state: this._mut.state,
      connectionStatus: this._mut.connectionStatus,
      effort: this._mut.effort,
      speed: this._mut.speed,
      permissionMode: this._mut.permissionMode,
      historyHasMore: this._mut.historyHasMore,
      historyLoading: this._mut.historyLoading,
      currentSession: this._mut.currentSession,
      sessionReady: this._mut.sessionReady,
      attachmentRole: this._mut.attachmentRole,
      attachmentCount: this._mut.attachmentCount,
      pendingUserInputIds: [...this._pendingUserInputs],
      lastAcceptedClientMessageId: this._lastAcceptedClientMessageId,
    };
  }
}

function isPermissionMode(value: unknown): value is PermissionMode {
  return (
    value === 'default' ||
    value === 'acceptEdits' ||
    value === 'plan' ||
    value === 'bypassPermissions' ||
    value === 'dontAsk' ||
    value === 'auto'
  );
}

function isAgentSpeed(value: unknown): value is AgentSpeed {
  return value === 'normal' || value === 'fast';
}
